import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, List, Progress, Space, Typography, Upload, message, theme } from 'antd';
import { SendOutlined, ThunderboltOutlined, UploadOutlined } from '@ant-design/icons';
import { TDataset } from '../../../models/aibuilder/aibuilder';
import { ImageModel, ClassifyResult } from '../engine/useImageModel';
import { TextModel } from '../engine/useTextModel';
import { loadImage } from '../engine/loadImage';
import { INTRUS_IMAGES } from '../engine/intrusImages';
import AIBuilderChatBubble, { AI_BUILDER_BOT_NAME } from '../chat/AIBuilderChatBubble';
import { formatClassifyReply } from '../engine/formatClassifyReply';

export interface TestStationProps {
  dataset: TDataset;
  imageModel: ImageModel;
  textModel: TextModel;
  editable: boolean;
}

const TestStation: React.FC<TestStationProps> = ({ dataset, imageModel, textModel, editable }) => {
  const isText = dataset.data_type === 'text';
  const activeModel = isText ? textModel : imageModel;
  const [preview, setPreview] = useState('');
  const [isIntrus, setIsIntrus] = useState(false);
  const [sentence, setSentence] = useState('');
  type ChatExchange = { kidContent: React.ReactNode; results: ClassifyResult };
  const [exchanges, setExchanges] = useState<ChatExchange[]>([]);
  const [testing, setTesting] = useState(false);
  const createdBlobUrls = useRef<string[]>([]);

  useEffect(
    () => () => {
      createdBlobUrls.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const choose = (next: File) => {
    const url = URL.createObjectURL(next);
    createdBlobUrls.current.push(url);
    setPreview(url);
    setIsIntrus(false);
  };

  const chooseIntrus = () => {
    setPreview(INTRUS_IMAGES[Math.floor(Math.random() * INTRUS_IMAGES.length)]);
    setIsIntrus(true);
  };

  const test = async () => {
    if (!activeModel.isReady) {
      message.info('Entraînez d’abord le modèle.');
      return;
    }
    setTesting(true);
    try {
      if (isText) {
        const text = sentence.trim();
        if (!text) return;
        const results = await textModel.classify(text);
        setExchanges((prev) => [...prev, { kidContent: text, results }]);
        setSentence('');
      } else {
        if (!preview) return;
        const image = await loadImage(preview);
        const results = await imageModel.classify(image);
        setExchanges((prev) => [
          ...prev,
          {
            kidContent: (
              <img
                src={preview}
                alt="Image testée"
                style={{ maxWidth: 120, maxHeight: 90, objectFit: 'contain' }}
              />
            ),
            results,
          },
        ]);
        setPreview('');
        setIsIntrus(false);
      }
    } catch (error: any) {
      message.error(error?.message || 'Échec de la classification.');
    } finally {
      setTesting(false);
    }
  };

  const { token } = theme.useToken();
  const composerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: token.paddingSM,
    flexWrap: 'wrap',
    background: token.colorBgContainer,
    border: `2px solid ${token.colorPrimaryBorder}`,
    borderRadius: 999,
    padding: token.paddingXS,
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {!editable &&
        activeModel.status !== 'ready' &&
        activeModel.status !== 'loading' &&
        activeModel.status !== 'training' && (
          <Alert type="warning" showIcon message="Le modèle n'a pas pu être préparé automatiquement." />
        )}

      {editable && !activeModel.isReady && (
        <AIBuilderChatBubble
          role="bot"
          header={AI_BUILDER_BOT_NAME}
          content={`Entraîne-moi d'abord à l'étape Entraîner, puis reviens me tester ${
            isText ? 'avec une phrase' : 'avec une image'
          } !`}
        />
      )}

      {!editable && (activeModel.status === 'loading' || activeModel.status === 'training') && (
        <AIBuilderChatBubble role="bot" header={AI_BUILDER_BOT_NAME} loading />
      )}

      {activeModel.isReady && exchanges.length === 0 && (
        <AIBuilderChatBubble
          role="bot"
          header={AI_BUILDER_BOT_NAME}
          content={
            isText
              ? 'Je suis prêt ! Écris-moi une phrase et je te dirai à quel label elle ressemble.'
              : 'Je suis prêt ! Choisis une image et je te dirai à quel label elle ressemble.'
          }
        />
      )}

      {exchanges.map((exchange, index) => (
        <React.Fragment key={index}>
          <AIBuilderChatBubble role="kid" content={exchange.kidContent} />
          <AIBuilderChatBubble
            role="bot"
            content={
              <>
                <Typography.Text>{formatClassifyReply(exchange.results)}</Typography.Text>
                <List
                  size="small"
                  dataSource={exchange.results}
                  renderItem={(result) => (
                    <List.Item>
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12 }}>
                        <span style={{ minWidth: 80 }}>{result.label}</span>
                        <Progress percent={Math.round(result.confidence * 100)} size="small" style={{ flex: 1 }} />
                      </div>
                    </List.Item>
                  )}
                />
              </>
            }
          />
        </React.Fragment>
      ))}

      {testing && <AIBuilderChatBubble role="bot" loading />}

      {isIntrus && (
        <Typography.Text type="secondary">
          Image surprise, sans rapport avec tes catégories : ton IA va quand même donner une réponse — regarde si elle est sûre d’elle.
        </Typography.Text>
      )}

      <div style={composerStyle}>
        {isText ? (
          <Input
            aria-label="Phrase à tester"
            placeholder="Écris une phrase à tester…"
            variant="borderless"
            value={sentence}
            onChange={(e) => setSentence(e.target.value)}
            onPressEnter={test}
            style={{ flex: 1 }}
          />
        ) : (
          <>
            <Upload
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(next) => {
                choose(next);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} shape="round">
                Choisir une image
              </Button>
            </Upload>
            <Button icon={<ThunderboltOutlined />} shape="round" onClick={chooseIntrus}>
              Essayer un intrus
            </Button>
            {preview && (
              <img
                src={preview}
                alt="Aperçu à tester"
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: token.borderRadius }}
              />
            )}
            <span style={{ flex: 1 }} />
          </>
        )}
        <Button
          type="primary"
          shape="circle"
          icon={<SendOutlined />}
          aria-label="Envoyer"
          onClick={test}
          disabled={(isText ? !sentence.trim() : !preview) || !activeModel.isReady}
          loading={testing}
        />
      </div>
    </Space>
  );
};

export default TestStation;
